<script setup lang="ts">
import { computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { EcxTooltip } from '@ecx/ui'
import { InfoIcon, SearchIcon, CheckFilledIcon, WarningCircleIcon } from '@ecx/ui/icons'
import icTotal from '@/assets/bulkImport/icons/ic-total.svg'
import icTotalVehicles from '@/assets/bulkImport/icons/ic-total-vehicles.svg'
import icWithCard from '@/assets/bulkImport/icons/ic-with-card.svg'
import icWithoutCard from '@/assets/bulkImport/icons/ic-without-card.svg'
import icIgnored from '@/assets/bulkImport/icons/ic-ignored.svg'
import { useBulkImportStore } from '@/stores/bulkImport'
import BulkImportStatCard from '../shared/BulkImportStatCard.vue'
import BulkImportSummaryHero from '../shared/BulkImportSummaryHero.vue'
import BulkImportErrorList from '../errors/BulkImportErrorList.vue'

const { t } = useI18n()
const store = useBulkImportStore()

const totals = computed(() => store.totals)
const ignoredCount = computed(() => totals.value?.ignored ?? 0)
const blockedCount = computed(() => totals.value?.blocked ?? 0)
const blockedAlertText = computed(() =>
  t('corp.bulk_import.summary.blocked_alert', { count: blockedCount.value }, blockedCount.value),
)
const showIgnoredWarning = computed(() => ignoredCount.value > 0)
const hasNothingNew = computed(() => (totals.value?.valid ?? 0) === 0)
const isVehicles = computed(() => store.entity === 'vehicles')
const isCards = computed(() => store.entity === 'cards')
const isEdit = computed(() => store.mode === 'edit')

const titleText = computed(() => {
  if (isEdit.value) return t('corp.bulk_import.edit.summary.title')
  if (hasNothingNew.value) return t('corp.bulk_import.summary.empty_title')
  return t('corp.bulk_import.summary.title')
})

const subtitleText = computed(() => {
  if (isEdit.value) {
    if (isCards.value) return t('corp.bulk_import.edit.summary.subtitle_cards')
    return isVehicles.value
      ? t('corp.bulk_import.edit.summary.subtitle_vehicles')
      : t('corp.bulk_import.edit.summary.subtitle')
  }
  if (hasNothingNew.value) return t('corp.bulk_import.summary.empty_subtitle')
  return isVehicles.value
    ? t('corp.bulk_import.summary.subtitle_vehicles')
    : t('corp.bulk_import.summary.subtitle')
})

const ignoredAlertText = computed(() => {
  const count = ignoredCount.value
  if (isEdit.value) {
    if (isCards.value) return t('corp.bulk_import.edit.summary.ignored_alert_cards', { count }, count)
    return isVehicles.value
      ? t('corp.bulk_import.edit.summary.ignored_alert_vehicles', { count }, count)
      : t('corp.bulk_import.edit.summary.ignored_alert', { count }, count)
  }
  return isVehicles.value
    ? t('corp.bulk_import.summary.ignored_alert_vehicles', { count }, count)
    : t('corp.bulk_import.summary.ignored_alert', { count }, count)
})

function loadIgnoredIfNeeded() {
  if (ignoredCount.value > 0 && !isEdit.value) store.loadIgnoredPage(1)
}

onMounted(loadIgnoredIfNeeded)
watch(ignoredCount, loadIgnoredIfNeeded)
</script>

<template>
  <div class="flex flex-col flex-1">
    <div
      class="flex flex-col items-center justify-center text-center gap-3 px-6"
      :class="isEdit ? 'flex-1 py-6' : 'pt-16 pb-6'"
      data-testid="summary-hero"
    >
      <BulkImportSummaryHero class="mb-2" />

      <h2 class="text-2xl font-semibold text-grey-950">
        {{ titleText }}
      </h2>

      <p class="text-sm text-grey-600 max-w-sm">
        {{ subtitleText }}
      </p>
    </div>

    <div v-if="isEdit" class="flex shrink-0 gap-2 px-6 py-2">
      <BulkImportStatCard
        :value="totals?.total ?? 0"
        :label="t('corp.bulk_import.edit.summary.total_found')"
        :icon="SearchIcon"
        icon-color-class="text-royal-500"
        icon-bg-class="bg-royal-25"
        icon-ring-class="ring-1 ring-royal-100"
      />

      <BulkImportStatCard
        :value="totals?.valid ?? 0"
        :label="t('corp.bulk_import.edit.summary.with_changes')"
        :icon="CheckFilledIcon"
        icon-color-class="text-green-500"
        icon-bg-class="bg-green-25"
        icon-ring-class="ring-1 ring-green-300"
      />

      <BulkImportStatCard
        :value="totals?.ignored ?? 0"
        :label="t('corp.bulk_import.edit.summary.no_changes')"
        icon-bg-class="bg-grey-50"
        icon-ring-class="shadow-bulk-import-icon-ring-grey"
      >
        <template #icon>
          <img :src="icIgnored" alt="" class="w-5 h-5" />
        </template>
      </BulkImportStatCard>
    </div>

    <div v-else class="flex gap-2 px-6 py-2">
      <BulkImportStatCard
        :value="totals?.total ?? 0"
        :label="t('corp.bulk_import.summary.total_found')"
        icon-bg-class="bg-royal-25"
        icon-ring-class="ring-1 ring-royal-100"
      >
        <template #icon>
          <img :src="isVehicles ? icTotalVehicles : icTotal" alt="" class="w-5 h-5" />
        </template>
      </BulkImportStatCard>

      <BulkImportStatCard
        :value="totals?.with_card !== undefined ? totals.with_card : '-'"
        :label="t('corp.bulk_import.summary.with_card')"
        icon-bg-class="bg-green-25"
        icon-ring-class="ring-1 ring-green-300"
      >
        <template #icon>
          <img :src="icWithCard" alt="" class="w-5 h-5" />
        </template>
      </BulkImportStatCard>

      <BulkImportStatCard
        :value="totals?.without_card !== undefined ? totals.without_card : '-'"
        :label="t('corp.bulk_import.summary.without_card')"
        icon-bg-class="bg-royal-25"
        icon-ring-class="ring-1 ring-royal-100"
      >
        <template #icon>
          <img :src="icWithoutCard" alt="" class="w-5 h-5" />
        </template>
      </BulkImportStatCard>

      <BulkImportStatCard
        :value="totals?.ignored ?? 0"
        :label="t('corp.bulk_import.summary.ignored')"
        icon-bg-class="bg-grey-50"
        icon-ring-class="shadow-bulk-import-icon-ring-grey"
      >
        <template #icon>
          <img :src="icIgnored" alt="" class="w-5 h-5" />
        </template>
        <template #label>
          <span class="flex items-center gap-1">
            {{ t('corp.bulk_import.summary.ignored') }}
            <EcxTooltip :content="isVehicles ? t('corp.bulk_import.summary.ignored_tooltip_vehicles') : t('corp.bulk_import.summary.ignored_tooltip')" placement="top">
              <InfoIcon class="w-3 h-3 text-grey-400 cursor-default" aria-hidden="true" />
            </EcxTooltip>
          </span>
        </template>
      </BulkImportStatCard>
    </div>

    <div class="flex flex-col gap-2 px-6 pb-6 pt-2" :class="isEdit && 'shrink-0'">
      <div
        v-if="hasNothingNew && !isEdit"
        class="flex items-center gap-2 px-2 py-1.5 bg-grey-50 rounded-lg"
        data-testid="nothing-new-banner"
      >
        <InfoIcon class="w-5 h-5 text-grey-500 flex-shrink-0" aria-hidden="true" />
        <p class="text-label-xs text-grey-800">
          {{ t('corp.bulk_import.summary.empty_alert') }}
        </p>
      </div>

      <div
        v-if="showIgnoredWarning"
        class="flex items-center gap-2 px-2 py-1.5 bg-grey-50 rounded-lg"
        data-testid="ignored-warning-banner"
      >
        <img :src="icIgnored" alt="" class="w-5 h-5 flex-shrink-0" />
        <p class="text-label-xs text-grey-800">
          {{ ignoredAlertText }}
        </p>
      </div>

      <div
        v-if="blockedCount > 0"
        class="flex items-center gap-2 px-2 py-1.5 bg-red-25 rounded-lg"
        data-testid="blocked-warning-banner"
      >
        <WarningCircleIcon class="w-5 h-5 text-red-500 flex-shrink-0" aria-hidden="true" />
        <p class="text-label-xs text-red-900">
          {{ blockedAlertText }}
        </p>
      </div>

      <div v-if="!hasNothingNew && !isVehicles && !isEdit" class="flex items-center gap-2 px-2 py-1.5 bg-royal-25 rounded-lg">
        <InfoIcon class="w-5 h-5 text-royal-500 flex-shrink-0" aria-hidden="true" />
        <p class="text-label-xs text-royal-500">
          {{ t('corp.bulk_import.summary.invites_notice') }}
        </p>
      </div>
    </div>

    <div
      v-if="showIgnoredWarning && !isEdit"
      class="mx-6 mb-6 border border-grey-200 rounded-lg overflow-hidden"
      data-testid="ignored-list"
    >
      <div class="px-4 pt-4 pb-2">
        <span class="text-base font-normal text-grey-950">
          {{ t('corp.bulk_import.summary.ignored_list_title') }}
        </span>
      </div>

      <BulkImportErrorList
        :errors="store.ignoredRows"
        :pagination="store.ignoredPagination"
        @page-change="store.loadIgnoredPage"
      />
    </div>
  </div>
</template>
